import { useCallback, useEffect, useLayoutEffect, useRef, useState, type RefObject } from 'react';
import {
  useDebouncedValue,
  type HotelAsyncState,
  type HotelPlaceSuggestion,
  type HotelSearchValue,
  type HotelTravelerOption,
} from '@voll-intelligence/intelligence-ui-kit';
import type {
  TravelerDirectoryPort,
  TravelerSearchResult,
} from '../../../shared/schema/traveler_directory.js';
import {
  type TravelerRef,
  placeIconFor,
  placeSecondLine,
  type HotelPlaceOption,
  type HotelPlaceSuggestions,
  type HotelPlaceSuggestionsPort,
  type HotelPlannerPort,
} from '@voll-intelligence/genui-contract';
import { genUiT } from '../../../shared/ui/i18n.js';
import { resolveHotelSearchDraft } from './hotel_planner_view.js';
import { useMyLocation } from './use_my_location.js';
import {
  placeIdOf,
  travelerIdOf,
  useSearchMemory,
  withRemembered,
  type Memory,
} from './hotel_search_memory.js';

/** O IO da barra de busca do hotel, injetado por quem hospeda: o organismo da
 *  conversa repassa os `ports` da config, e uma página do host passa os dela
 *  sem precisar de `GenUiConfigProvider`. Mesmas chaves e formas de
 *  `GenUiConfig['ports']`, só com o que a barra lê; cada port ausente degrada
 *  como antes (sem busca de lugar, sem viajantes, sem sugestões, sem "Minha
 *  localização"). O `hotelPlaceSuggestions` precisa de identidade estável:
 *  trocá-lo relê as sugestões e descarta as do port anterior. */
export type HotelSearchOptionsPorts = {
  readonly hotelPlanner?: Pick<
    HotelPlannerPort,
    'searchHotelPlaces' | 'findHotelPlaceByCoordinate'
  >;
  readonly travelers?: TravelerDirectoryPort;
  readonly hotelPlaceSuggestions?: HotelPlaceSuggestionsPort;
};

type UseHotelSearchOptionsInput = {
  ports: HotelSearchOptionsPorts;
  initialTravelers: readonly TravelerRef[];
  initialPlace?: HotelPlaceOption;
  selectedPlaceId?: string;
  selectedTravelerIds?: readonly string[];
  requesterId?: string;
  locale: string;
};

/** Nenhuma sugestão ainda (sem port, ou port ainda não respondeu) — constante
 *  de módulo pela mesma razão do `NONE` do par de voo: identidade estável. */
const NO_SUGGESTIONS: HotelPlaceSuggestions = { recent: [], favorite: [] };

type QueryGeneration = {
  raw: string;
  generation: number;
};

function mergeTravelers(
  initial: readonly TravelerRef[],
  results: readonly TravelerSearchResult[],
): TravelerSearchResult[] {
  const merged = new Map<string, TravelerSearchResult>();
  initial.forEach((traveler) => {
    merged.set(traveler.id, traveler);
  });
  results.forEach((traveler) => {
    merged.set(traveler.id, traveler);
  });
  return [...merged.values()];
}

function buildPlaceCatalog(
  catalog: Memory<HotelPlaceOption>,
  places: readonly HotelPlaceOption[],
  selectedPlaceId?: string,
  initialPlace?: HotelPlaceOption,
): HotelPlaceOption[] {
  const rememberedPlace = selectedPlaceId ? catalog.get(selectedPlaceId) : undefined;
  return [
    ...(initialPlace ? [initialPlace] : []),
    ...(rememberedPlace ? [rememberedPlace] : []),
    ...places,
  ].filter(
    (place, index, allPlaces) =>
      allPlaces.findIndex((candidate) => candidate.placeId === place.placeId) === index,
  );
}

function requestIsCurrent(
  sequence: number,
  sequenceRef: RefObject<number>,
  generation: number,
  generationRef: RefObject<number>,
): boolean {
  return sequence === sequenceRef.current && generation === generationRef.current;
}

/** A linha do campo `Onde`: nome em cima, `tipo · endereço` embaixo e o glifo do
 *  tipo — as três chamadas do designer (2026-08-06 as duas linhas, 2026-08-19 o
 *  relógio no grupo de histórico). `fromHistory` é do GRUPO, não do lugar: dentro
 *  de Recentes a procedência pesa mais que o tipo, que segue escrito na segunda
 *  linha. */
function toPlaceSuggestions(
  places: readonly HotelPlaceOption[],
  locale: string,
  groupLabel?: string,
  fromHistory = false,
): HotelPlaceSuggestion[] {
  return places.map((place) => {
    const description = placeSecondLine(place.types, place.subtitle ?? place.address, locale);
    return {
      id: place.placeId,
      label: place.title,
      icon: placeIconFor(place.types, fromHistory),
      ...(description ? { description } : {}),
      ...(groupLabel
        ? { groupLabel }
        : place.classification
          ? { groupLabel: place.classification }
          : {}),
    };
  });
}

/** O que o catálogo do lugar deve LEMBRAR (Map, nunca esquece) além da busca
 *  corrente: o inicial/selecionado, e as duas procedências de sugestão — pra
 *  `resolveValue` conseguir resolver um placeId escolhido bem depois de a
 *  digitação ter substituído a exibição. */
function pinnedPlaces(
  initialPlace: HotelPlaceOption | undefined,
  suggestions: HotelPlaceSuggestions,
  places: readonly HotelPlaceOption[],
): HotelPlaceOption[] {
  return [
    ...(initialPlace ? [initialPlace] : []),
    ...suggestions.recent,
    ...suggestions.favorite,
    ...places,
  ];
}

/** Lê `hotelPlaceSuggestions` UMA vez por port, no mesmo molde de
 *  `useRecentAirports` (par de voo): sem port, ou se a leitura falhar,
 *  degrada pra `NO_SUGGESTIONS` — o campo de destino segue só com busca por
 *  texto. Também lembra os dois conjuntos (+ o inicial/selecionado) no Map
 *  de catálogo, pro `resolveValue` conseguir resolver um placeId escolhido
 *  mesmo depois de a exibição zero-query sair de cena. Extraído da
 *  `useHotelSearchOptions` só para caber no teto de linhas por função. */
function useHotelPlaceSuggestions(
  ports: HotelSearchOptionsPorts,
  remember: (places: readonly HotelPlaceOption[]) => void,
): HotelPlaceSuggestions {
  const port = ports.hotelPlaceSuggestions;
  // As sugestões são DO port que as respondeu: trocá-lo volta a `NO_SUGGESTIONS`
  // no mesmo render, sem as do port anterior.
  const [answer, setAnswer] = useState<{
    port: HotelPlaceSuggestionsPort;
    suggestions: HotelPlaceSuggestions;
  } | null>(null);
  useEffect(() => {
    if (!port) return undefined;
    let alive = true;
    port
      .getHotelPlaceSuggestions()
      .then((fetched) => {
        if (!alive) return;
        setAnswer({ port, suggestions: fetched });
        remember([...fetched.recent, ...fetched.favorite]);
      })
      .catch(() => {
        // Degrade silencioso: o campo volta a ser só busca por texto. A rota já
        // é fail-open por fonte — sugestão ausente não é erro consertável.
      });
    return () => {
      alive = false;
    };
  }, [port, remember]);
  return answer && answer.port === port ? answer.suggestions : NO_SUGGESTIONS;
}

/** Busca de lugar debounced com guarda de sequência/geração — mesma forma da
 *  busca de viajantes (não extraída junto: tem paginação própria e o teto de
 *  linhas cabe sem ela). Extraído da `useHotelSearchOptions` só pelo teto de
 *  linhas por função; `sequenceRef`/`generationRef` são os MESMOS refs da
 *  função de fora, então `onPlaceQueryChange` (que os incrementa) continua
 *  invalidando uma busca em voo do jeito que sempre invalidou. `answeredQuery`
 *  é o termo que `places`/`placeSearchState` RESPONDEM — é por ele que a
 *  exibição sabe se a lista é do que está digitado agora (`placeViewFor`). */
function usePlaceSearch(
  searchRef: RefObject<((query: string) => Promise<HotelPlaceOption[]>) | undefined>,
  debouncedPlace: QueryGeneration,
  sequenceRef: RefObject<number>,
  generationRef: RefObject<number>,
  remember: (places: readonly HotelPlaceOption[]) => void,
): { places: HotelPlaceOption[]; placeSearchState: HotelAsyncState; answeredQuery: string } {
  const [places, setPlaces] = useState<HotelPlaceOption[]>([]);
  const [placeSearchState, setPlaceSearchState] = useState<HotelAsyncState>('idle');
  const [answeredQuery, setAnsweredQuery] = useState('');
  const debouncedPlaceQuery = debouncedPlace.raw.trim();
  useEffect(() => {
    const search = searchRef.current;
    const sequence = ++sequenceRef.current;
    const generation = debouncedPlace.generation;
    if (!search || debouncedPlaceQuery.length < 2) {
      setPlaces([]);
      setPlaceSearchState(search ? 'ready' : 'idle');
      setAnsweredQuery(debouncedPlaceQuery);
      return;
    }
    setPlaceSearchState('loading');
    search(debouncedPlaceQuery)
      .then((items) => {
        if (!requestIsCurrent(sequence, sequenceRef, generation, generationRef)) return;
        remember(items);
        setPlaces(items);
        setPlaceSearchState('ready');
        setAnsweredQuery(debouncedPlaceQuery);
      })
      .catch(() => {
        if (!requestIsCurrent(sequence, sequenceRef, generation, generationRef)) return;
        setPlaces([]);
        setPlaceSearchState('error');
        setAnsweredQuery(debouncedPlaceQuery);
      });
  }, [
    debouncedPlace.generation,
    debouncedPlaceQuery,
    searchRef,
    sequenceRef,
    generationRef,
    remember,
  ]);
  return { places, placeSearchState, answeredQuery };
}

/** Minha localização chega fora do ciclo de busca por termo (ver
 *  `use_my_location.ts`) — por isso não passa por `useHotelPlaceSuggestions`.
 *  O lugar real resolvido entra no MESMO Map de sugestões pra `resolveValue`
 *  achá-lo do mesmo jeito que acha um recente ou um favorito escolhido. */
function useMyLocationRequest(
  ports: HotelSearchOptionsPorts,
  remember: (places: readonly HotelPlaceOption[]) => void,
): {
  myLocationState: 'idle' | 'loading' | 'error';
  requestMyLocation: () => Promise<HotelPlaceOption | null>;
  cancelMyLocation: () => void;
} {
  const myLocation = useMyLocation(ports.hotelPlanner?.findHotelPlaceByCoordinate);
  const request = myLocation.request;
  const requestMyLocation = useCallback(async () => {
    const place = await request();
    if (place) remember([place]);
    return place;
  }, [request, remember]);
  return {
    myLocationState: myLocation.state,
    requestMyLocation,
    cancelMyLocation: myLocation.cancel,
  };
}

/** Exibição zero-query (campo vazio): recentes e favoritos ROTULADOS depois do
 *  que já está selecionado/inicial — senão a escolha corrente desaparece da
 *  lista só por abrir o popover sem digitar. Com termo, quem responde é
 *  `placeViewFor`.
 *
 *  O que já está selecionado pode SER um recente/favorito (o usuário
 *  escolheu um dos dois e reabriu o campo sem digitar): sem o filtro abaixo,
 *  o mesmo placeId sairia duas vezes — uma sem grupo (via `placeCatalog`),
 *  outra sob "Recentes"/"Lugares favoritos" — e como `PlaceResults` agrupa por
 *  `groupLabel` em `CommandGroup`s separados, são duas LINHAS visíveis, não
 *  um key duplicado inofensivo. O lugar que "Minha localização" virou é uma
 *  escolha como outra qualquer: reaberto o campo, ele aparece como o
 *  selecionado, e `resolveValue` o acha pelo Map (ver `withRememberedPlace`). */
function zeroQuerySuggestions(
  placeCatalog: readonly HotelPlaceOption[],
  suggestions: HotelPlaceSuggestions,
  locale: string,
): HotelPlaceSuggestion[] {
  const suggestedIds = new Set(
    [...suggestions.recent, ...suggestions.favorite].map((place) => place.placeId),
  );
  const catalogWithoutSuggested = placeCatalog.filter((place) => !suggestedIds.has(place.placeId));
  // Um lugar pode ter as DUAS procedências: as duas lanes deduplicam dentro de
  // si, nenhuma contra a outra. Salvar um lugar em "Lugares favoritos" e depois
  // buscar por ele é o caso de uso principal da feature, e sem este corte ele
  // sairia duas vezes — duas LINHAS visíveis, porque os grupos são
  // `CommandGroup`s separados, com dois itens do cmdk carregando o mesmo valor.
  // Quem fica com ele é "Lugares favoritos": favoritar é gesto deliberado do
  // viajante, aparecer em recentes é consequência de ter buscado.
  const favoriteIds = new Set(suggestions.favorite.map((place) => place.placeId));
  const recentWithoutFavorite = suggestions.recent.filter(
    (place) => !favoriteIds.has(place.placeId),
  );
  return [
    ...toPlaceSuggestions(catalogWithoutSuggested, locale),
    ...toPlaceSuggestions(
      recentWithoutFavorite,
      locale,
      genUiT(locale, 'hotelPlanner.recentPlaces'),
      true,
    ),
    ...toPlaceSuggestions(
      suggestions.favorite,
      locale,
      genUiT(locale, 'hotelPlanner.favoritePlaces'),
    ),
  ];
}

/** O que o `Onde` MOSTRA para o termo VIVO (o que está no campo agora, antes do
 *  debounce). Sem termo, a exibição zero-query. Com termo, só a resposta DAQUELE
 *  termo, sem o destino corrente fixado no topo: o cmdk realça a linha #0, e o
 *  Enter escolhia o valor antigo por cima do que a pessoa digitou (A5 do QA de
 *  29/09). Enquanto a resposta não chega (debounce, busca em voo), `loading` e
 *  nenhuma linha — o Enter não tem o que escolher, e o texto fica no campo.
 *  Abaixo do mínimo da busca não há resposta a esperar: nenhuma linha. */
function placeViewFor(
  search: {
    live: string;
    answeredQuery: string;
    state: HotelAsyncState;
    places: readonly HotelPlaceOption[];
  },
  { canSearch, locale }: { canSearch: boolean; locale: string },
  zeroQuery: () => HotelPlaceSuggestion[],
): { placeSuggestions: HotelPlaceSuggestion[]; placeSearchState: HotelAsyncState } {
  const idle: HotelAsyncState = canSearch ? 'ready' : 'idle';
  if (search.live === '') return { placeSuggestions: zeroQuery(), placeSearchState: idle };
  if (search.live.length < 2) return { placeSuggestions: [], placeSearchState: idle };
  if (search.answeredQuery !== search.live || search.state === 'loading') {
    return { placeSuggestions: [], placeSearchState: 'loading' };
  }
  return {
    placeSuggestions: toPlaceSuggestions(search.places, locale),
    placeSearchState: search.state,
  };
}

/** `placeCatalog` é o catálogo da query corrente (a busca + o inicial/selecionado)
 *  — some quando a busca muda. Recentes, favoritos e o lugar de Minha localização, porém, têm que
 *  resolver mesmo depois de saírem de cena (usuário escolheu, depois digitou
 *  algo, o rascunho ainda referencia o placeId escolhido); por isso o Map
 *  (que `rememberPlaces` nunca esquece) entra como fallback só do id que o
 *  valor está resolvendo. */
function withRememberedPlace(
  places: readonly HotelPlaceOption[],
  catalog: Memory<HotelPlaceOption>,
  selectedPlaceId?: string,
): readonly HotelPlaceOption[] {
  const remembered = selectedPlaceId ? catalog.get(selectedPlaceId) : undefined;
  if (!remembered || places.some((option) => option.placeId === remembered.placeId)) {
    return places;
  }
  return [...places, remembered];
}

/** O solicitante do bloco é `{id,name}` cru; a foto dele vem do host
 *  (`viewerPhotoUrl`, #2640) e entra aqui antes do catálogo — como no campo QUEM
 *  do voo, ele raramente está na 1ª página da busca alfabética. */
function withViewerPhoto(
  travelers: readonly TravelerRef[],
  requesterId: string | undefined,
  viewerPhotoUrl: string | null | undefined,
): TravelerSearchResult[] {
  if (!requesterId || !viewerPhotoUrl) return [...travelers];
  return travelers.map((traveler) =>
    traveler.id === requesterId ? { ...traveler, photoUrl: viewerPhotoUrl } : traveler,
  );
}

function toTravelerOptions(travelers: readonly TravelerSearchResult[]): HotelTravelerOption[] {
  return travelers.map((traveler) => ({
    id: traveler.id,
    label: traveler.name,
    ...(traveler.subtitle ? { description: traveler.subtitle } : {}),
    ...(traveler.photoUrl ? { photoUrl: traveler.photoUrl } : {}),
  }));
}

function findTravelers(
  catalog: Memory<TravelerSearchResult>,
  ids: readonly string[],
): TravelerSearchResult[] {
  return ids.flatMap((id) => {
    const traveler = catalog.get(id);
    return traveler ? [traveler] : [];
  });
}

type ResolvedDraft = ReturnType<typeof resolveHotelSearchDraft>;
type ResolvedDraftCache = { key: string; value: ResolvedDraft };

function stableResolvedDraft(
  cache: RefObject<ResolvedDraftCache | null>,
  value: HotelSearchValue,
  catalog: Parameters<typeof resolveHotelSearchDraft>[1],
): ResolvedDraft {
  const resolved = resolveHotelSearchDraft(value, catalog);
  const key = JSON.stringify(resolved);
  if (cache.current?.key === key) return cache.current.value;
  cache.current = { key, value: resolved };
  return resolved;
}

/** A busca de viajantes (Quem): o termo debounced com guarda de sequência/geração,
 *  a paginação e o "a mesma consulta não é mudança". Fora da `useHotelSearchOptions`
 *  pelo teto de linhas dela; o que chega entra na memória (`remember`). */
function useTravelerSearch(
  travelerSearch: TravelerDirectoryPort['searchTravelers'] | undefined,
  rememberTravelers: (travelers: readonly TravelerSearchResult[]) => void,
) {
  // Por ref (renovada a cada commit): só o efeito e o "carregar mais" a chamam.
  const travelerSearchRef = useRef(travelerSearch);
  useLayoutEffect(() => {
    travelerSearchRef.current = travelerSearch;
  });
  const [travelerQuery, setTravelerQuery] = useState<QueryGeneration>({ raw: '', generation: 0 });
  const debouncedTraveler = useDebouncedValue(travelerQuery, 300);
  const debouncedTravelerQuery = debouncedTraveler.raw.trim();
  const [travelerResults, setTravelerResults] = useState<TravelerSearchResult[]>([]);
  const [travelerSearchState, setTravelerSearchState] = useState<HotelAsyncState>(
    travelerSearch ? 'loading' : 'ready',
  );
  const [travelerPage, setTravelerPage] = useState(0);
  const [hasMoreTravelers, setHasMoreTravelers] = useState(false);
  const travelerSequence = useRef(0);
  const travelerGenerationRef = useRef(0);
  const travelerRawRef = useRef('');

  useEffect(() => {
    const search = travelerSearchRef.current;
    const sequence = ++travelerSequence.current;
    const generation = debouncedTraveler.generation;
    if (!search) {
      setTravelerResults([]);
      setHasMoreTravelers(false);
      setTravelerSearchState('ready');
      return;
    }
    setTravelerSearchState('loading');
    search({ ...(debouncedTravelerQuery ? { term: debouncedTravelerQuery } : {}), page: 0 })
      .then((response) => {
        if (!requestIsCurrent(sequence, travelerSequence, generation, travelerGenerationRef))
          return;
        rememberTravelers(response.items);
        setTravelerResults(response.items);
        setHasMoreTravelers(response.hasNext);
        setTravelerPage(0);
        setTravelerSearchState('ready');
      })
      .catch(() => {
        if (!requestIsCurrent(sequence, travelerSequence, generation, travelerGenerationRef))
          return;
        setTravelerResults([]);
        setHasMoreTravelers(false);
        setTravelerSearchState('error');
      });
  }, [debouncedTraveler.generation, debouncedTravelerQuery, rememberTravelers]);

  const onLoadMoreTravelers = useCallback(() => {
    const search = travelerSearchRef.current;
    const generation = debouncedTraveler.generation;
    if (!search || !hasMoreTravelers || generation !== travelerGenerationRef.current) return;
    const sequence = ++travelerSequence.current;
    const nextPage = travelerPage + 1;
    setTravelerSearchState('loading');
    search({ ...(debouncedTravelerQuery ? { term: debouncedTravelerQuery } : {}), page: nextPage })
      .then((response) => {
        if (!requestIsCurrent(sequence, travelerSequence, generation, travelerGenerationRef))
          return;
        rememberTravelers(response.items);
        setTravelerResults((current) => mergeTravelers(current, response.items));
        setHasMoreTravelers(response.hasNext);
        setTravelerPage(nextPage);
        setTravelerSearchState('ready');
      })
      .catch(() => {
        if (!requestIsCurrent(sequence, travelerSequence, generation, travelerGenerationRef))
          return;
        setTravelerSearchState('error');
      });
  }, [
    debouncedTraveler.generation,
    debouncedTravelerQuery,
    hasMoreTravelers,
    travelerPage,
    rememberTravelers,
  ]);

  const onTravelerQueryChange = useCallback((query: string) => {
    // A MESMA consulta de novo não é mudança: o picker de hóspedes zera o
    // termo ao fechar (e ao abrir) mesmo com ele já vazio, e cada bump de
    // geração refazia `GET /business/travelers` — dezenas de chamadas numa
    // sessão sem nada ter mudado (#3217). A geração continua a subir a cada
    // edição REAL, que é o que protege contra a resposta atrasada. Compara o
    // termo APARADO: é ele que vira o `term` do pedido (`debouncedTravelerQuery`),
    // então " Ana " depois de "Ana" também não é mudança.
    const term = query.trim();
    if (travelerRawRef.current === term) return;
    travelerRawRef.current = term;
    travelerSequence.current += 1;
    travelerGenerationRef.current += 1;
    setHasMoreTravelers(false);
    setTravelerQuery({ raw: query, generation: travelerGenerationRef.current });
  }, []);
  return {
    travelerResults,
    travelerSearchState,
    hasMoreTravelers,
    onTravelerQueryChange,
    onLoadMoreTravelers,
  };
}

export function useHotelSearchOptions({
  ports,
  initialTravelers,
  initialPlace,
  selectedPlaceId,
  selectedTravelerIds = [],
  requesterId,
  locale,
}: UseHotelSearchOptionsInput) {
  const viewerPhotoUrl = ports.travelers?.viewerPhotoUrl;
  const seededTravelers = withViewerPhoto(initialTravelers, requesterId, viewerPhotoUrl);
  const placeSearch = ports.hotelPlanner?.searchHotelPlaces;
  // A busca do port por ref (renovada a cada commit): só o efeito a chama, e um
  // port recriado pelo host não refaz a busca em curso.
  const placeSearchRef = useRef(placeSearch);
  useLayoutEffect(() => {
    placeSearchRef.current = placeSearch;
  });
  const [placeQuery, setPlaceQuery] = useState<QueryGeneration>({ raw: '', generation: 0 });
  const debouncedPlace = useDebouncedValue(placeQuery, 300);
  const placeSequence = useRef(0);
  const placeGenerationRef = useRef(0);
  const resolvedDraftRef = useRef<ResolvedDraftCache | null>(null);
  const { travelerMemory, placeMemory, rememberTravelers, rememberPlaces } = useSearchMemory(
    seededTravelers,
    initialPlace,
  );

  const { places, placeSearchState, answeredQuery } = usePlaceSearch(
    placeSearchRef,
    debouncedPlace,
    placeSequence,
    placeGenerationRef,
    rememberPlaces,
  );
  const suggestions = useHotelPlaceSuggestions(ports, rememberPlaces);
  const { myLocationState, requestMyLocation, cancelMyLocation } = useMyLocationRequest(
    ports,
    rememberPlaces,
  );
  // O que a barra conhece agora: a memória, com o que está em cena por cima (o
  // semeado/inicial, as sugestões e a busca corrente — o último visto vale).
  const knownTravelers = withRemembered(travelerMemory, seededTravelers, travelerIdOf);
  const {
    travelerResults,
    travelerSearchState,
    hasMoreTravelers,
    onTravelerQueryChange,
    onLoadMoreTravelers,
  } = useTravelerSearch(ports.travelers?.searchTravelers, rememberTravelers);
  const knownPlaces = withRemembered(
    placeMemory,
    pinnedPlaces(initialPlace, suggestions, places),
    placeIdOf,
  );

  const selectedTravelers = findTravelers(knownTravelers, selectedTravelerIds);
  // A busca e o catálogo guardam o solicitante como a API o devolve (sem foto):
  // a foto do host é reaplicada por cima do merge para não voltar às iniciais.
  const travelerCatalog = withViewerPhoto(
    mergeTravelers(mergeTravelers(seededTravelers, selectedTravelers), travelerResults),
    requesterId,
    viewerPhotoUrl,
  );
  const placeCatalog = buildPlaceCatalog(knownPlaces, places, selectedPlaceId, initialPlace);
  const placeView = placeViewFor(
    { live: placeQuery.raw.trim(), answeredQuery, state: placeSearchState, places },
    { canSearch: placeSearch !== undefined, locale },
    () => zeroQuerySuggestions(placeCatalog, suggestions, locale),
  );
  const travelers = toTravelerOptions(travelerCatalog);

  return {
    ...placeView,
    travelers,
    travelerSearchState,
    hasMoreTravelers,
    onPlaceQueryChange: useCallback((query: string) => {
      placeSequence.current += 1;
      placeGenerationRef.current += 1;
      setPlaceQuery({ raw: query, generation: placeGenerationRef.current });
    }, []),
    onTravelerQueryChange,
    onLoadMoreTravelers,
    myLocationState,
    requestMyLocation,
    cancelMyLocation,
    resolveValue: (value: HotelSearchValue) =>
      stableResolvedDraft(resolvedDraftRef, value, {
        places: withRememberedPlace(placeCatalog, knownPlaces, value.selectedPlaceId),
        travelers: [...knownTravelers.values()],
        requesterId,
      }),
  };
}
